import { V2_ZONE_META } from "./v2OverviewUtils";

export const MUSCLE_DATA_KEYS = {
  quadriceps: "quadriceps",
  quads: "quadriceps",
  hamstrings: "hamstring",
  hamstring: "hamstring",
  adductors: "adductors",
  calves: "calf",
  calf: "calf",
  glutes: "gluteal",
  gluteal: "gluteal",
  iliopsoas: "iliopsoas",
};

export const MUSCLE_I18N_KEYS = {
  quadriceps: "quadriceps",
  hamstrings: "hamstrings",
  hamstring: "hamstrings",
  adductors: "adductors",
  calves: "calves",
  calf: "calves",
  glutes: "gluteal",
  gluteal: "gluteal",
  iliopsoas: "iliopsoas",
};

export const MUSCLE_GROUPS = ["quadriceps", "hamstring", "adductors", "calf", "gluteal", "iliopsoas"];

export const MUSCLE_TOOLTIP_KEYS = {
  gluteal: "glutes",
  glutes: "glutes",
  hamstring: "hamstrings",
  hamstrings: "hamstrings",
  quadriceps: "quads",
  quads: "quads",
  calf: "calves",
  calves: "calves",
  adductors: "adductors",
  iliopsoas: "iliopsoas",
};

function sideKey(side) {
  if (side === "R" || side === "right") return "right";
  return "left";
}

function emptySide() {
  return { left: null, right: null };
}

function emptyGroup() {
  return {
    intensity_index: emptySide(),
    mechanical_load_index: emptySide(),
    fatigue: emptySide(),
    trainability: emptySide(),
    trainability_status: emptySide(),
    trainability_by_mode: {
      load: { left: null, right: null, status: emptySide() },
      intensity: { left: null, right: null, status: emptySide() },
    },
  };
}

function cloneSide(side) {
  return {
    left: side?.left ?? null,
    right: side?.right ?? null,
  };
}

function cloneStatus(status) {
  return {
    left: status?.left || null,
    right: status?.right || null,
  };
}

function cloneMode(mode, fallback) {
  return {
    left: mode?.left ?? fallback?.left ?? null,
    right: mode?.right ?? fallback?.right ?? null,
    status: cloneStatus(mode?.status || fallback?.status),
  };
}

function cloneGroup(group) {
  if (!group) return emptyGroup();
  const trainability = cloneSide(group.trainability);
  const status = cloneStatus(group.trainability_status);
  return {
    intensity_index: cloneSide(group.intensity_index),
    mechanical_load_index: cloneSide(group.mechanical_load_index),
    fatigue: cloneSide(group.fatigue),
    trainability,
    trainability_status: status,
    trainability_by_mode: {
      load: cloneMode(group.trainability_by_mode?.load, { ...trainability, status }),
      intensity: cloneMode(group.trainability_by_mode?.intensity, { ...trainability, status }),
    },
  };
}

function applyTreatItem(muscleData, item) {
  const group = MUSCLE_DATA_KEYS[item.muscle];
  const side = sideKey(item.side);
  if (!muscleData[group]) return;
  muscleData[group].trainability[side] = item.score;
  muscleData[group].trainability_status[side] = item.zone;
  muscleData[group].trainability_by_mode.load[side] = item.score;
  muscleData[group].trainability_by_mode.load.status[side] = item.zone;
  muscleData[group].trainability_by_mode.intensity[side] = item.score;
  muscleData[group].trainability_by_mode.intensity.status[side] = item.zone;
}

export function mapMuscleSnapshot(payload, player) {
  const muscleData = Object.fromEntries(MUSCLE_GROUPS.map((group) => [
    group,
    cloneGroup(payload?.[group]),
  ]));
  if (!payload) {
    (player?.musclesToTreat || []).forEach((item) => applyTreatItem(muscleData, item));
  }
  muscleData.trainability_global_status = {
    load: payload?.trainability_global_status?.load || player?.zone || null,
    intensity: payload?.trainability_global_status?.intensity || player?.zone || null,
  };
  muscleData.global_trainability_status =
    payload?.global_trainability_status
    || muscleData.trainability_global_status.load
    || player?.zone
    || null;
  return muscleData;
}

export function normalizeMuscleSelection(muscle, player) {
  if (!muscle) return null;
  const name = muscle.muscle || muscle.name;
  const side = muscle.side === "right" || muscle.side === "R" ? "R" : "L";
  const fromList = (player.musclesToTreat || []).find((item) => (
    MUSCLE_DATA_KEYS[item.muscle] === MUSCLE_DATA_KEYS[name]
    && item.side === side
  ));
  return {
    muscle: name,
    side,
    score: fromList?.score ?? muscle.trainability ?? muscle.score ?? player.score,
    zone: fromList?.zone ?? muscle.trainability_status ?? muscle.zone ?? player.zone,
    period: fromList?.period || "week",
    playerName: player.name,
  };
}

export function zoneColor(zone) {
  return V2_ZONE_META[zone]?.color || "#8581bc";
}
